// src/pages/leaderboardsPage.js
// Public leaderboards (/leaderboards). Anyone can see the boards; signed-in
// learners also get "your standing" cards. Prerendered by react-snap for
// search engines: during the prerender the boards come from
// /leaderboards-snapshot.json (written by scripts/prerender.mjs, since the
// prerender blocks API calls), and the rows are embedded in the page as JSON so
// the browser's first render matches the prerendered HTML before it fetches
// the live boards.
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { BiBookReader, BiBullseye, BiBoltCircle, BiSolidFlame } from 'react-icons/bi';
import Navbar from '../components/navBar';
import Footer from '../components/footer';
import Leaderboard from '../components/leaderboard';
import { usePostgresUser } from '../components/utils/PostgresUserContext';
import { backendService } from '../services/api/backendService';
import { getToken } from '../config/firebase';

const PAGE_URL = 'https://onikanji.com/leaderboards';
const OG_IMAGE = 'https://onikanji.com/og-image.png';
const PAGE_TITLE = 'Kanji Leaderboards: Top Japanese Learners by Reviews, Accuracy & Streak | OniKanji';
const PAGE_DESCRIPTION =
  'See the top OniKanji learners: most kanji reviews, highest accuracy, fastest level speed runs and longest study streaks. Updated every night. Sign up free to get your name on the board.';
const SNAPSHOT_ID = 'leaderboards-data';

const IS_PRERENDER = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

// The API calls the accuracy board userStats; the component calls it totalAccuracy.
const toBoards = (data) => ({
  mostReviews: data?.leaderboards?.mostReviews || [],
  totalAccuracy: data?.leaderboards?.userStats || [],
  speedRun: data?.leaderboards?.speedRun || [],
  streak: data?.leaderboards?.streak || [],
  outputBattle: data?.leaderboards?.outputBattle || [],
  lastUpdated: data?.leaderboards?.mostReviews?.[0]?.lastUpdated || null,
});

// The rows embedded by the prerendered page, if this is a hydrating load.
const readEmbedded = () => {
  if (typeof document === 'undefined') return null;
  try {
    const el = document.getElementById(SNAPSHOT_ID);
    return el ? JSON.parse(el.textContent) : null;
  } catch (e) {
    return null;
  }
};

// Safe inside <script>: no "</script>" or "<!--" can come from a username.
const embedJson = (value) => JSON.stringify(value).replace(/</g, '\\u003c');

const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

const BOARD_EXPLAINERS = [
  { icon: BiBookReader, title: 'Most Reviews', text: 'Total kanji reviews completed. Accuracy breaks ties. Every review you do counts.' },
  { icon: BiBullseye, title: 'Total Accuracy', text: 'Overall review accuracy, for learners with 500+ reviews so the number means something.' },
  { icon: BiBoltCircle, title: 'Speed Run', text: 'Highest level reached, then the fewest review minutes it took. For learners who started at Level 1.' },
  { icon: BiSolidFlame, title: 'Longest Streak', text: 'The longest run of consecutive days with reviews each learner has ever reached.' },
];

function LeaderboardsPage() {
  const { user, publicUsername, subscriptionStatus } = usePostgresUser();
  const [boards, setBoards] = useState(() => readEmbedded());
  const [standing, setStanding] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (IS_PRERENDER) {
          const res = await fetch('/leaderboards-snapshot.json');
          if (!cancelled && res.ok) setBoards(toBoards(await res.json()));
          return;
        }
        const live = toBoards(await backendService.getPublicLeaderboards(100));
        if (cancelled) return;
        setBoards(live);
        // Leaderboard messages are a nice-to-have; the boards stand without them.
        try {
          const names = [...new Set(['mostReviews', 'totalAccuracy', 'speedRun', 'streak', 'outputBattle']
            .flatMap((k) => live[k].map((e) => e.publicUsername)).filter(Boolean))];
          const msgs = names.length ? await backendService.getAllLeaderboardMessages(names) : null;
          if (cancelled || !msgs?.success) return;
          const byName = new Map((msgs.data || []).map((m) => [m.publicUsername, m]));
          const attach = (rows) => rows.map((e) => ({ ...e, leaderboardMessage: byName.get(e.publicUsername) || null }));
          setBoards((b) => b && {
            ...b,
            mostReviews: attach(b.mostReviews), totalAccuracy: attach(b.totalAccuracy),
            speedRun: attach(b.speedRun), streak: attach(b.streak), outputBattle: attach(b.outputBattle),
          });
        } catch (e) { /* messages are optional */ }
      } catch (e) {
        // Keep whatever the prerendered page had.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user) { setStanding(null); return; }
    let cancelled = false;
    backendService.getLeaderboardStanding(getToken())
      .then((res) => { if (!cancelled) setStanding(res?.boards || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const topTen = (boards?.mostReviews || []).slice(0, 10);
  const updated = boards?.lastUpdated ? new Date(boards.lastUpdated) : null;

  return (
    <div className="font-noto-sans-jp flex flex-col min-h-screen bg-mainBackgroundColor">
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:title" content="Kanji Leaderboards | OniKanji" />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="OniKanji" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kanji Leaderboards | OniKanji" />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'OniKanji', item: 'https://onikanji.com/' },
            { '@type': 'ListItem', position: 2, name: 'Leaderboards', item: PAGE_URL },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'OniKanji Leaderboards',
          url: PAGE_URL,
          description: PAGE_DESCRIPTION,
          isPartOf: { '@type': 'WebSite', name: 'OniKanji', url: 'https://onikanji.com' },
          ...(topTen.length ? {
            mainEntity: {
              '@type': 'ItemList',
              name: 'Most kanji reviews',
              itemListOrder: 'https://schema.org/ItemListOrderDescending',
              numberOfItems: topTen.length,
              itemListElement: topTen.map((e) => ({
                '@type': 'ListItem',
                position: e.position,
                name: `${e.publicUsername}: ${fmt(e.totalReviews)} reviews`,
              })),
            },
          } : {}),
        })}</script>
      </Helmet>

      <Navbar user={user} subscriptionStatus={subscriptionStatus} />

      <main className="container mx-auto px-4 sm:px-6 pb-12 flex-grow">
        <header className="text-center mt-6 mb-8 max-sm:mt-4 max-sm:mb-5">
          <h1 className="text-3xl md:text-4xl font-bold text-logoColor max-sm:text-2xl">Kanji Leaderboards</h1>
          <p className="text-gray-600 max-w-2xl mx-auto mt-2 max-sm:text-sm max-sm:mt-1">
            The top OniKanji learners by reviews, accuracy, speed and streak.
            {updated && <> Updated nightly; last updated {updated.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}.</>}
          </p>
        </header>

        {user ? (
          <StandingCards standing={standing} />
        ) : (
          <section className="mb-8 bg-white rounded-xl shadow-blueBoxShadow p-6 text-center max-sm:p-4 max-sm:mb-5">
            <h2 className="text-xl font-bold text-logoColor mb-1">Want your name up here?</h2>
            <p className="text-gray-600 mb-4">Every kanji review counts toward the Most Reviews board. It's free to start.</p>
            <Link
              to="/signup"
              className="inline-block px-6 py-3 bg-darkOrangeColor hover:bg-darkOrangeColorButtonHover text-white font-semibold rounded-lg shadow-md"
            >
              Start learning kanji free →
            </Link>
          </section>
        )}

        <Leaderboard
          currentPage="leaderboards"
          totalAccuracyData={boards?.totalAccuracy || []}
          speedRunData={boards?.speedRun || []}
          streakData={boards?.streak || []}
          mostReviewsData={boards?.mostReviews || []}
          outputBattleData={boards?.outputBattle || []}
          highlightUsername={user ? publicUsername : null}
        />

        <section className="mt-10 max-sm:mt-6">
          <h2 className="text-2xl font-bold text-logoColor mb-4 text-center max-sm:text-xl max-sm:mb-3">How the boards work</h2>
          <div className="grid sm:grid-cols-2 gap-4 max-sm:gap-2">
            {BOARD_EXPLAINERS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 max-sm:p-4">
                <h3 className="flex items-center gap-2 font-bold text-darkBlueColor mb-1">
                  <Icon className="text-xl" /> {title}
                </h3>
                <p className="text-sm text-gray-600">{text}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 text-center mt-4">
            Each board shows the top 100 learners and is rebuilt every night. Set a leaderboard message from your own row.
          </p>
        </section>
      </main>

      {/* The prerendered rows, so hydration renders exactly what's in the HTML. */}
      {boards && (
        <script
          type="application/json"
          id={SNAPSHOT_ID}
          dangerouslySetInnerHTML={{ __html: embedJson(boards) }}
        />
      )}

      <Footer />
    </div>
  );
}

// ── Your standing ───────────────────────────────────────────────────────────

const StandingCards = ({ standing }) => {
  if (!standing) {
    return <div className="mb-8 h-36 rounded-xl bg-white/60 animate-pulse" aria-hidden="true" />;
  }
  const cards = [
    { key: 'mostReviews', title: 'Most Reviews', icon: BiBookReader, ...mostReviewsCard(standing.mostReviews) },
    { key: 'totalAccuracy', title: 'Total Accuracy', icon: BiBullseye, ...accuracyCard(standing.totalAccuracy) },
    { key: 'speedRun', title: 'Speed Run', icon: BiBoltCircle, ...speedRunCard(standing.speedRun) },
    { key: 'streak', title: 'Longest Streak', icon: BiSolidFlame, ...streakCard(standing.streak) },
  ];
  return (
    <section className="mb-8 max-sm:mb-5">
      <h2 className="text-xl font-bold text-logoColor mb-3 max-sm:text-lg max-sm:mb-2">Your standing</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-sm:grid-cols-2 max-sm:gap-2">
        {cards.map(({ key, title, icon: Icon, rank, value, next }) => (
          <div key={key} className="bg-white rounded-xl shadow-blueBoxShadow p-5 flex flex-col max-sm:p-3 max-sm:min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-2 max-sm:text-xs max-sm:gap-1.5 max-sm:mb-1">
              <Icon className="text-lg text-darkBlueColor" /> {title}
            </div>
            <div className={`text-3xl font-bold ${rank ? 'text-logoColor' : 'text-gray-400'} max-sm:text-xl`}>
              {rank ? `#${rank}` : 'Not ranked'}
            </div>
            {value && <div className="text-sm text-gray-600 mt-0.5 max-sm:text-xs">{value}</div>}
            {next && <div className="text-sm text-darkBlueColor font-medium mt-3 pt-3 border-t border-gray-100 max-sm:text-xs max-sm:mt-2 max-sm:pt-2">{next}</div>}
          </div>
        ))}
      </div>
    </section>
  );
};

// Each returns { rank, value, next }. Boards rebuild nightly, so a gap that has
// already closed reads as "passed, moves you up tonight".
function mostReviewsCard(b) {
  if (!b) return {};
  const value = `${fmt(b.live)} reviews`;
  if (b.position === 1) return { rank: 1, value, next: "You're #1. Keep reviewing to hold it." };
  if (b.position && b.above) {
    const gap = b.above.value - b.live + 1;
    return {
      rank: b.position, value,
      next: gap > 0
        ? `${fmt(gap)} reviews to pass ${b.above.publicUsername} (#${b.above.position})`
        : `You've passed ${b.above.publicUsername}. You move up at tonight's update.`,
    };
  }
  if (b.lastPlace) {
    const gap = b.lastPlace.value - b.live + 1;
    return { value, next: gap > 0 ? `${fmt(gap)} more reviews to make the top 100` : "You'll join the board at tonight's update." };
  }
  return { value, next: b.live > 0 ? "You'll join the board at tonight's update." : 'Do your first review to join the board.' };
}

function accuracyCard(b) {
  if (!b) return {};
  if (!b.position && b.reviewsToQualify > 0) {
    return { value: b.live ? `${pct(b.live)} so far` : null, next: `${fmt(b.reviewsToQualify)} more reviews to qualify (500 needed)` };
  }
  const value = pct(b.live);
  if (b.position === 1) return { rank: 1, value, next: 'The most accurate learner on OniKanji.' };
  if (b.position && b.above) {
    const points = (b.above.value - b.live) * 100;
    return {
      rank: b.position, value,
      next: points > 0
        ? `${points.toFixed(1)} points behind ${b.above.publicUsername} (#${b.above.position})`
        : `You've caught ${b.above.publicUsername}. You move up at tonight's update.`,
    };
  }
  if (b.lastPlace) return { value, next: `Reach ${pct(b.lastPlace.value)} accuracy to make the top 100` };
  return { value, next: "You'll join the board at tonight's update." };
}

function speedRunCard(b) {
  if (!b) return {};
  if (b.excluded) return { next: "This board is for learners who started at Level 1, so skipping ahead takes you off it." };
  const value = `Level ${b.live.level} · ${fmt(Math.round(b.live.minutes))} min of reviews`;
  if (b.position === 1) return { rank: 1, value, next: 'The fastest climber on OniKanji.' };
  if (b.position && b.above) {
    return {
      rank: b.position, value,
      next: b.above.value.level > b.live.level
        ? `${b.above.publicUsername} (#${b.above.position}) is on Level ${b.above.value.level}. Level up to close the gap.`
        : `${b.above.publicUsername} (#${b.above.position}) reached Level ${b.above.value.level} in fewer minutes. Level up to pass them.`,
    };
  }
  if (b.lastPlace) return { value, next: `Reach Level ${b.lastPlace.value.level + 1} to make the top 100` };
  return { value, next: b.live.minutes > 0 ? "You'll join the board at tonight's update." : 'Do your first review to join the board.' };
}

function streakCard(b) {
  if (!b) return {};
  const value = `Best ${fmt(b.live)} days · current ${fmt(b.current)}`;
  if (b.position === 1) return { rank: 1, value, next: 'The longest streak on OniKanji.' };
  if (b.position && b.above) {
    const days = b.above.value + 1 - b.current;
    return {
      rank: b.position, value,
      next: days <= 0
        ? `You've passed ${b.above.publicUsername}. You move up at tonight's update.`
        : b.current > 0
          ? `Keep your streak going ${fmt(days)} more day${days === 1 ? '' : 's'} to pass ${b.above.publicUsername} (#${b.above.position})`
          : `Study ${fmt(days)} days in a row to pass ${b.above.publicUsername} (#${b.above.position})`,
    };
  }
  if (b.lastPlace) {
    const days = b.lastPlace.value + 1 - b.current;
    return {
      value,
      next: days > 0
        ? `Study ${fmt(days)} more day${days === 1 ? '' : 's'} in a row to make the top 100`
        : "You'll join the board at tonight's update.",
    };
  }
  return { value, next: b.current > 0 ? "You'll join the board at tonight's update." : 'Review today to start a streak.' };
}

export default LeaderboardsPage;
